import { deriveGcsUrls, type GcsConfig } from '../../types/gcs/gcsConfig';
import type { ConnectionState } from '../../types/general/connectionState';
import { LaunchpadSchema, type Launchpad } from '../../types/gcs/launchpad';
import { FlightNetworkSchema, type FlightNetwork } from '../../types/gcs/flightNetwork';
import {
  CenterSchema,
  type Center,
  CenterOpenStateNotificationSchema,
  type CenterOpenStateNotification,
  type SetCenterOpenStateRequest,
  SetCenterOpenStateRequestSchema
} from '../../types/gcs/center';
import {
  MissionCreatedNotificationSchema,
  MissionUpdatedNotificationSchema,
  MissionFinishedNotificationSchema,
  MissionDetailedStateNotificationSchema,
  MissionFlightDataNotificationSchema,
  MissionPreflightStateNotificationSchema,
  MissionLockingInfoSchema,
  MissionRouteNotificationSchema,
  MissionFailureModeRouteInfoSchema,
  MissionEtasNotificationSchema,
  MissionTimestampsNotificationSchema,
  MissionRpicInControlNotificationSchema,
  MissionLogMessageNotificationSchema,
  MissionProgressNotificationSchema,
  type MissionCreatedNotification,
  type MissionUpdatedNotification,
  type MissionFinishedNotification,
  type MissionDetailedStateNotification,
  type MissionFlightDataNotification,
  type MissionPreflightStateNotification,
  type MissionLockingInfoNotification,
  type MissionId,
  type MissionRouteNotification,
  type MissionFailureModeRouteInfo,
  type MissionEtasNotification,
  type MissionTimestampsNotification,
  type MissionRpicInControlNotification,
  type MissionLogMessageNotification,
  type MissionProgressNotification,
  SimpleMissionRequestSchema,
  SetMissionNoteRequestSchema,
  MissionNoteResponseSchema
} from '../../types/gcs/mission';
import {
  LaunchpadAssignmentsNotificationSchema,
  type LaunchpadAssignmentsNotification
} from '../../types/gcs/launchpadAssignment';
import {
  VehicleSchema,
  VehicleStateNotificationSchema,
  VehicleMissionComputerStatusNotificationSchema,
  VehiclesPreparationStatusNotificationSchema,
  type Vehicle,
  type VehicleStateNotification,
  type VehicleMissionComputerStatusNotification,
  type VehicleModuleVersion,
  type VehiclesPreparationStatusNotification,
  type VehicleId,
  SimpleVehicleRequestSchema,
  VehicleModuleVersionSchema
} from '../../types/gcs/vehicle';
import {
  ForeignAircraftDetailsNotificationSchema,
  ForeignAircraftRiskVolumeNotificationSchema,
  type ForeignAircraftDetailsNotification,
  type ForeignAircraftRiskVolumeNotification
} from '../../types/gcs/foreignAircraft';
import {
  PreflightOverviewNotificationSchema,
  PreflightMissionAtLaunchpadNotificationSchema,
  PreflightMissionAtDockNotificationSchema,
  type PreflightOverviewNotification,
  type PreflightMissionAtLaunchpadNotification,
  type PreflightMissionAtDockNotification,
  CancelPreflightRequestSchema
} from '../../types/gcs/preflight';
import {
  GetPostflightMissionSummaryRequestSchema,
  PostflightMissionSummarySchema,
  type PostflightMissionSummary
} from '../../types/gcs/postflight';
import {
  UssConnectionStateNotificationSchema,
  UssLogMessageNotificationSchema,
  type UssConnectionStateNotification,
  type UssLogMessageNotification
} from '../../types/gcs/uss';
import { GcsWebSocket } from './GcsWebSocket';
import { httpClient } from '../../services/http/httpClient';
import { createLogger, type Logger } from '../../utils/logger';
import { IsLoggedInResponseSchema, SignInWithOktaRequestSchema, UserSchema, type User } from '../../types/gcs/user';
import { Schema } from 'effect';
import { VersionSchema, type Version } from '../../types/gcs/version';
import { SubmitVehicleDiscrepancyReportRequestSchema } from '../../types/gcs/discrepancyReport';

export class GcsConnection {
  private config: GcsConfig;
  private ws: GcsWebSocket;
  private logger: Logger;
  private httpBaseUrl: string;

  // Topic constants
  private static readonly TOPIC_VERSION = 'version';
  private static readonly TOPIC_USERS = 'users';
  private static readonly TOPIC_VEHICLES = 'vehicles';
  private static readonly TOPIC_LAUNCHPADS = 'launchpads';
  private static readonly TOPIC_FLIGHT_NETWORK = 'flight_network';
  private static readonly TOPIC_CENTERS = 'centers';
  private static readonly TOPIC_CENTER_OPEN_STATE = 'center_open_state';
  private static readonly TOPIC_LIVE_MISSIONS = 'live_missions';
  private static readonly TOPIC_MISSION_STATE = 'mission_state';
  private static readonly TOPIC_MISSION_COMMAND = 'mission_command';
  private static readonly TOPIC_PREFLIGHT_MISSION_OVERVIEW = 'preflight_mission_overview';
  private static readonly TOPIC_PREFLIGHT_MISSION = 'preflight_mission';
  private static readonly TOPIC_PREFLIGHT_MISSION_AT_DOCK = 'preflight_mission_at_dock';
  private static readonly TOPIC_MISSION_POSTFLIGHT = 'mission_postflight';
  private static readonly TOPIC_LAUNCHPAD_ASSIGNMENT = 'launchpad_assignment';
  private static readonly TOPIC_VEHICLE_STATE = 'vehicle_state';
  private static readonly TOPIC_VEHICLE_MISSION_COMPUTER_STATUS = 'mc_status';
  private static readonly TOPIC_VEHICLE_MISSION_COMPUTER = 'mission_computer';
  private static readonly TOPIC_VEHICLE_PREPARATION = 'vehicle_preparation';
  private static readonly TOPIC_FOREIGN_AIRCRAFT = 'foreign_aircraft';
  private static readonly TOPIC_USS = 'uss';
  private static readonly TOPIC_DISCREPANCY_REPORTS = 'discrepancy_reports';
  private static readonly TOPIC_MISSION_NOTES = 'mission_notes';

  constructor(config: GcsConfig) {
    this.config = config;
    const { httpBaseUrl, wsUrl } = deriveGcsUrls(config.baseUrl);
    this.httpBaseUrl = httpBaseUrl;
    this.ws = new GcsWebSocket(wsUrl, config.id);
    this.logger = createLogger(config.id);
  }

  get id(): string {
    return this.config.id;
  }

  get connectionState(): ConnectionState {
    return this.ws.state;
  }

  // Connection management
  connect(queryParams?: Record<string, string>): void {
    this.logger.info('Connecting...');
    this.ws.connect(queryParams);
  }

  disconnect(): void {
    this.logger.info('Disconnecting...');
    this.ws.disconnect();
  }

  listenToConnectionStateChanged(handler: (state: ConnectionState, error?: string) => void): () => void {
    return this.ws.addStateChangeHandler(handler);
  }

  // ============================================================================
  // REST Endpoints
  // ============================================================================

  async get<ID, IE, OD, OE>(
    path: string,
    schemaI: Schema.Schema<ID, IE, never>,
    schemaO: Schema.Schema<OD, OE, never>,
    input: ID
  ): Promise<OD> {
    const response = await httpClient.get<Schema.Schema.Encoded<typeof schemaO>>(`${this.httpBaseUrl}/${path}`, {
      credentials: 'include',
      body: Schema.encodeSync(schemaI)(input)
    });
    return Schema.decodeUnknownSync(schemaO)(response);
  }

  async post<ID, IE, OD, OE>(
    path: string,
    schemaI: Schema.Schema<ID, IE, never>,
    schemaO: Schema.Schema<OD, OE, never>,
    input: ID
  ): Promise<OD> {
    const response = await httpClient.post<Schema.Schema.Encoded<typeof schemaO>>(`${this.httpBaseUrl}/${path}`, {
      credentials: 'include',
      body: Schema.encodeSync(schemaI)(input)
    });
    return Schema.decodeUnknownSync(schemaO)(response);
  }

  async patch<ID, IE, OD, OE>(
    path: string,
    schemaI: Schema.Schema<ID, IE, never>,
    schemaO: Schema.Schema<OD, OE, never>,
    input: ID
  ): Promise<OD> {
    const response = await httpClient.patch<Schema.Schema.Encoded<typeof schemaO>>(`${this.httpBaseUrl}/${path}`, {
      credentials: 'include',
      body: Schema.encodeSync(schemaI)(input)
    });
    return Schema.decodeUnknownSync(schemaO)(response);
  }

  async isLoggedIn(): Promise<boolean> {
    const response = await this.post(
      `${GcsConnection.TOPIC_USERS}/is_logged_in/`,
      Schema.Void,
      IsLoggedInResponseSchema,
      undefined
    );
    return response.isLoggedIn;
  }

  async fetchCurrentUser(): Promise<User> {
    return this.get(`${GcsConnection.TOPIC_USERS}/current/`, Schema.Void, UserSchema, undefined);
  }

  async signInWithOkta(code: string, redirectUri: string | undefined): Promise<void> {
    await this.post(`${GcsConnection.TOPIC_USERS}/okta_callback/`, SignInWithOktaRequestSchema, Schema.Void, {
      code,
      redirectUri: redirectUri ?? ''
    });
  }

  async signOut(): Promise<void> {
    await this.post(`${GcsConnection.TOPIC_USERS}/logout/`, Schema.Void, Schema.Void, undefined);
  }

  async fetchVersion(): Promise<Version> {
    return this.get(`${GcsConnection.TOPIC_VERSION}/`, Schema.Void, VersionSchema, undefined);
  }

  async fetchVehicles(): Promise<readonly Vehicle[]> {
    return this.get(`${GcsConnection.TOPIC_VEHICLES}/`, Schema.Void, Schema.Array(VehicleSchema), undefined);
  }

  async fetchVehicleAllModuleVersions(vehicleId: VehicleId): Promise<readonly VehicleModuleVersion[]> {
    return this.post(
      `${GcsConnection.TOPIC_VEHICLES}/${vehicleId}/all_module_versions/`,
      Schema.Void,
      Schema.Array(VehicleModuleVersionSchema),
      undefined
    );
  }

  async fetchLaunchpads(): Promise<readonly Launchpad[]> {
    return this.get(`${GcsConnection.TOPIC_LAUNCHPADS}/`, Schema.Void, Schema.Array(LaunchpadSchema), undefined);
  }

  async fetchFlightNetwork(): Promise<FlightNetwork> {
    return this.get(`${GcsConnection.TOPIC_FLIGHT_NETWORK}/`, Schema.Void, FlightNetworkSchema, undefined);
  }

  async fetchCenter(): Promise<Center> {
    return this.get(`${GcsConnection.TOPIC_CENTERS}/1/`, Schema.Void, CenterSchema, undefined);
  }

  async submitDiscrepancyReport(vehicleId: VehicleId, description: string): Promise<void> {
    return this.post(
      `${GcsConnection.TOPIC_DISCREPANCY_REPORTS}/`,
      SubmitVehicleDiscrepancyReportRequestSchema,
      Schema.Void,
      { vehicleId, description }
    );
  }

  async fetchMissionNote(missionId: MissionId): Promise<string | null> {
    const response = await this.get(
      `${GcsConnection.TOPIC_MISSION_NOTES}/${missionId}/`,
      Schema.Void,
      MissionNoteResponseSchema,
      undefined
    );
    return response.missionNote;
  }

  async setMissionNote(missionId: MissionId, note: string): Promise<void> {
    return this.patch(`${GcsConnection.TOPIC_MISSION_NOTES}/${missionId}/`, SetMissionNoteRequestSchema, Schema.Void, {
      missionNote: note
    });
  }

  // ============================================================================
  // WebSocket JRPC Subscriptions
  // ============================================================================

  async subscribeToTopic(topicName: string): Promise<void> {
    this.logger.info(`Subscribing to topic: ${topicName}`);
    return this.ws.call(`${topicName}.subscribe`, Schema.Any, Schema.Void, undefined);
  }

  async subscribeToTopicWithArg<ID, IE>(
    topicName: string,
    schema: Schema.Schema<ID, IE, never>,
    arg: ID
  ): Promise<void> {
    this.logger.info(`Subscribing to topic: ${topicName}`, arg);
    return this.ws.call(`${topicName}.subscribe`, schema, Schema.Void, arg);
  }

  async unsubscribeFromTopic(topicName: string): Promise<void> {
    this.logger.info(`Unsubscribing from topic: ${topicName}`);
    return this.ws.call(`${topicName}.unsubscribe`, Schema.Any, Schema.Void, undefined);
  }

  async unsubscribeFromTopicWithArg<ID, IE>(
    topicName: string,
    schema: Schema.Schema<ID, IE, never>,
    arg: ID
  ): Promise<void> {
    this.logger.info(`Unsubscribing from topic: ${topicName}`, arg);
    return this.ws.call(`${topicName}.unsubscribe`, schema, Schema.Void, arg);
  }

  addNotificationHandler<D, E>(
    method: string,
    schema: Schema.Schema<D, E, never>,
    handler: (data: D) => void
  ): () => void {
    return this.ws.addNotificationHandler(method, schema, handler);
  }

  addTopicChangedNotificationHandler<D, E>(
    topicName: string,
    schema: Schema.Schema<D, E, never>,
    handler: (data: D) => void
  ): () => void {
    return this.addNotificationHandler<D, E>(`${topicName}.changed`, schema, handler);
  }

  async subscribeToCenterOpenState(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_CENTER_OPEN_STATE);
  }

  listenToCenterOpenStateChanged(handler: (notification: CenterOpenStateNotification) => void): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_CENTER_OPEN_STATE,
      CenterOpenStateNotificationSchema,
      handler
    );
  }

  async setCenterOpenState(request: SetCenterOpenStateRequest): Promise<void> {
    return this.ws.call(
      `${GcsConnection.TOPIC_CENTER_OPEN_STATE}.set`,
      SetCenterOpenStateRequestSchema,
      Schema.Void,
      request
    );
  }

  async subscribeToLiveMissions(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_LIVE_MISSIONS);
  }

  listenToMissionCreatedNotification(handler: (notification: MissionCreatedNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_LIVE_MISSIONS}.created`,
      MissionCreatedNotificationSchema,
      handler
    );
  }

  listenToMissionUpdatedNotification(handler: (notification: MissionUpdatedNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_LIVE_MISSIONS}.updated`,
      MissionUpdatedNotificationSchema,
      handler
    );
  }

  listenToMissionFinishedNotification(handler: (notification: MissionFinishedNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_LIVE_MISSIONS}.finished`,
      MissionFinishedNotificationSchema,
      handler
    );
  }

  async subscribeToMissionState(missionId: MissionId): Promise<void> {
    return this.subscribeToTopicWithArg(GcsConnection.TOPIC_MISSION_STATE, SimpleMissionRequestSchema, { missionId });
  }

  async unsubscribeFromMissionState(missionId: MissionId): Promise<void> {
    return this.unsubscribeFromTopicWithArg(GcsConnection.TOPIC_MISSION_STATE, SimpleMissionRequestSchema, {
      missionId
    });
  }

  listenToMissionDetailedState(handler: (notification: MissionDetailedStateNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.detailed`,
      MissionDetailedStateNotificationSchema,
      handler
    );
  }

  listenToMissionRoute(handler: (notification: MissionRouteNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.route`,
      MissionRouteNotificationSchema,
      handler
    );
  }

  listenToMissionFailureModeRouteInfo(handler: (notification: MissionFailureModeRouteInfo) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.failure_mode_route_info`,
      MissionFailureModeRouteInfoSchema,
      handler
    );
  }

  listenToMissionLockingInfo(handler: (notification: MissionLockingInfoNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.locking_info`,
      MissionLockingInfoSchema,
      handler
    );
  }

  listenToMissionFlightData(handler: (notification: MissionFlightDataNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.flight_data`,
      MissionFlightDataNotificationSchema,
      handler
    );
  }

  listenToMissionPreflightState(handler: (notification: MissionPreflightStateNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.preflight_state`,
      MissionPreflightStateNotificationSchema,
      handler
    );
  }

  listenToMissionEtas(handler: (notification: MissionEtasNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.etas`,
      MissionEtasNotificationSchema,
      handler
    );
  }

  listenToMissionTimestamps(handler: (notification: MissionTimestampsNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.timestamps`,
      MissionTimestampsNotificationSchema,
      handler
    );
  }

  listenToMissionRpicInControl(handler: (notification: MissionRpicInControlNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.rpic_in_control`,
      MissionRpicInControlNotificationSchema,
      handler
    );
  }

  listenToMissionLogMessage(handler: (notification: MissionLogMessageNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.log_message`,
      MissionLogMessageNotificationSchema,
      handler
    );
  }

  listenToMissionProgress(handler: (notification: MissionProgressNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_MISSION_STATE}.progress`,
      MissionProgressNotificationSchema,
      handler
    );
  }

  async subscribeToPreflightMissionOverview(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_PREFLIGHT_MISSION_OVERVIEW);
  }

  listenToPreflightMissionOverviewChanged(handler: (notification: PreflightOverviewNotification) => void): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_PREFLIGHT_MISSION_OVERVIEW,
      PreflightOverviewNotificationSchema,
      handler
    );
  }

  async subscribeToPreflightMissionAtLaunchpad(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_PREFLIGHT_MISSION);
  }

  listenToPreflightMissionAtLaunchpadChanged(
    handler: (notification: PreflightMissionAtLaunchpadNotification) => void
  ): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_PREFLIGHT_MISSION,
      PreflightMissionAtLaunchpadNotificationSchema,
      handler
    );
  }

  async subscribeToPreflightMissionAtDock(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_PREFLIGHT_MISSION_AT_DOCK);
  }

  listenToPreflightMissionAtDockChanged(
    handler: (notification: PreflightMissionAtDockNotification) => void
  ): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_PREFLIGHT_MISSION_AT_DOCK,
      PreflightMissionAtDockNotificationSchema,
      handler
    );
  }

  async subscribeToLaunchpadAssignment(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_LAUNCHPAD_ASSIGNMENT);
  }

  listenToLaunchpadAssignmentChanged(handler: (notification: LaunchpadAssignmentsNotification) => void): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_LAUNCHPAD_ASSIGNMENT,
      LaunchpadAssignmentsNotificationSchema,
      handler
    );
  }

  async subscribeToVehicleState(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_VEHICLE_STATE);
  }

  listenToVehicleStateChanged(handler: (notification: VehicleStateNotification) => void): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_VEHICLE_STATE,
      VehicleStateNotificationSchema,
      handler
    );
  }

  async subscribeToVehicleMissionComputerStatus(vehicleId: VehicleId): Promise<void> {
    return this.subscribeToTopicWithArg(
      GcsConnection.TOPIC_VEHICLE_MISSION_COMPUTER_STATUS,
      SimpleVehicleRequestSchema,
      { vehicleId }
    );
  }

  listenToVehicleMissionComputerStatusChanged(
    handler: (notification: VehicleMissionComputerStatusNotification) => void
  ): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_VEHICLE_MISSION_COMPUTER_STATUS,
      VehicleMissionComputerStatusNotificationSchema,
      handler
    );
  }

  async subscribeToVehiclePreparationStatus(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_VEHICLE_PREPARATION);
  }

  listenToVehiclesPreparationStatusChanged(
    handler: (notification: VehiclesPreparationStatusNotification) => void
  ): () => void {
    return this.addTopicChangedNotificationHandler(
      GcsConnection.TOPIC_VEHICLE_PREPARATION,
      VehiclesPreparationStatusNotificationSchema,
      handler
    );
  }

  async subscribeToForeignAircraft(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_FOREIGN_AIRCRAFT);
  }

  listenToForeignAircraftDetailsChanged(
    handler: (notification: ForeignAircraftDetailsNotification) => void
  ): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_FOREIGN_AIRCRAFT}.details_changed`,
      ForeignAircraftDetailsNotificationSchema,
      handler
    );
  }

  listenToForeignAircraftRiskVolumeChanged(
    handler: (notification: ForeignAircraftRiskVolumeNotification) => void
  ): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_FOREIGN_AIRCRAFT}.risk_volume_changed`,
      ForeignAircraftRiskVolumeNotificationSchema,
      handler
    );
  }

  async approveTakeoff(missionId: MissionId): Promise<void> {
    return this.ws.call(
      `${GcsConnection.TOPIC_PREFLIGHT_MISSION}.approve_takeoff`,
      SimpleMissionRequestSchema,
      Schema.Void,
      { missionId }
    );
  }

  async approveTakeoffFromDock(missionId: MissionId): Promise<void> {
    return this.ws.call(
      `${GcsConnection.TOPIC_PREFLIGHT_MISSION_AT_DOCK}.approve_takeoff`,
      SimpleMissionRequestSchema,
      Schema.Void,
      { missionId }
    );
  }

  async cancelPreflight(missionId: MissionId, requeuePackage: boolean): Promise<void> {
    return this.ws.call(`${GcsConnection.TOPIC_PREFLIGHT_MISSION}.cancel`, CancelPreflightRequestSchema, Schema.Void, {
      missionId,
      requeuePackage
    });
  }

  async cancelDockPreflight(missionId: MissionId, requeuePackage: boolean): Promise<void> {
    return this.ws.call(
      `${GcsConnection.TOPIC_PREFLIGHT_MISSION_AT_DOCK}.cancel`,
      CancelPreflightRequestSchema,
      Schema.Void,
      { missionId, requeuePackage }
    );
  }

  async rerouteHomeFromDock(missionId: MissionId): Promise<void> {
    return this.ws.call(
      `${GcsConnection.TOPIC_PREFLIGHT_MISSION_AT_DOCK}.reroute_home_from_dock`,
      SimpleMissionRequestSchema,
      Schema.Void,
      {
        missionId
      }
    );
  }

  private async sendSimpleVehicleCommand(command: string, vehicleId: VehicleId): Promise<void> {
    return this.ws.call(
      `${GcsConnection.TOPIC_VEHICLE_MISSION_COMPUTER}.${command}`,
      SimpleVehicleRequestSchema,
      Schema.Void,
      { vehicleId }
    );
  }

  async restartVehicle(vehicleId: VehicleId): Promise<void> {
    return this.sendSimpleVehicleCommand('restart', vehicleId);
  }

  async disconnectVehicle(vehicleId: VehicleId): Promise<void> {
    return this.sendSimpleVehicleCommand('disconnect', vehicleId);
  }

  async triggerRemoteIdFailure(vehicleId: VehicleId): Promise<void> {
    return this.sendSimpleVehicleCommand('trigger_remote_id_failure', vehicleId);
  }

  private async sendSimpleMissionCommand(command: string, missionId: MissionId): Promise<void> {
    return this.ws.call(`${GcsConnection.TOPIC_MISSION_COMMAND}.${command}`, SimpleMissionRequestSchema, Schema.Void, {
      missionId
    });
  }

  async safeUp(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('safe_up', missionId);
  }

  async rtl(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('abort_mission_return_home', missionId);
  }

  async emergencyLand(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('abort_mission_land', missionId);
  }

  async triggerParachute(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('trigger_parachute', missionId);
  }

  async triggerFcLoss(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('trigger_fc_loss', missionId);
  }

  async triggerDaa(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('trigger_daa', missionId);
  }

  async triggerMotorFailure(vehicleId: VehicleId): Promise<void> {
    return this.sendSimpleVehicleCommand('trigger_motor_failure', vehicleId);
  }

  async triggerVeryLowBattery(vehicleId: VehicleId): Promise<void> {
    return this.sendSimpleVehicleCommand('trigger_very_low_battery', vehicleId);
  }

  async triggerVibrationExceedance(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('trigger_vibration_exceedance', missionId);
  }

  async triggerVeering(missionId: MissionId): Promise<void> {
    return this.sendSimpleMissionCommand('trigger_veering', missionId);
  }

  async triggerGpsDegradation(vehicleId: VehicleId): Promise<void> {
    return this.sendSimpleVehicleCommand('trigger_gps_degradation', vehicleId);
  }

  async getPostflightMissionSummary(missionId: MissionId): Promise<PostflightMissionSummary> {
    return this.ws.call(
      `${GcsConnection.TOPIC_MISSION_POSTFLIGHT}.get_mission_summary`,
      GetPostflightMissionSummaryRequestSchema,
      PostflightMissionSummarySchema,
      { missionId }
    );
  }

  async subscribeToUss(): Promise<void> {
    return this.subscribeToTopic(GcsConnection.TOPIC_USS);
  }

  listenToUssConnectionState(handler: (notification: UssConnectionStateNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_USS}.connection_state`,
      UssConnectionStateNotificationSchema,
      handler
    );
  }

  listenToUssLogMessage(handler: (notification: UssLogMessageNotification) => void): () => void {
    return this.addNotificationHandler(
      `${GcsConnection.TOPIC_USS}.log_message`,
      UssLogMessageNotificationSchema,
      handler
    );
  }
}
